import {useEffect, useRef} from 'react';
import {useLocation, useNavigationType} from 'react-router';

import NAVIGATION_TYPES from '@/consts/navigationTypes';
import {setNavigationDepth} from '@/services/route/routerService';

interface HistoryState {
    depth?: number;
}

const readDepth = (): number => (window.history.state as HistoryState | null)?.depth ?? 0;

const writeDepth = (depth: number): void => {
    window.history.replaceState({...window.history.state, depth}, '');
};

/**
 * Tracks navigation history depth and syncs it to routerService.
 * Depth is persisted in `history.state` so Back/Forward read accurate values.
 */
const useNavigationTracker = () => {
    const location = useLocation();
    const navigationType = useNavigationType();

    const depthRef = useRef(0);
    const lastKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (lastKeyRef.current === location.key) return;
        lastKeyRef.current = location.key;

        let newDepth = depthRef.current;

        switch (navigationType) {
            case NAVIGATION_TYPES.PUSH:
                newDepth = depthRef.current + 1;
                writeDepth(newDepth);
                break;
            case NAVIGATION_TYPES.POP:
                // History.state holds the exact depth for both Back and Forward.
                newDepth = readDepth();
                break;
            case NAVIGATION_TYPES.REPLACE:
                writeDepth(newDepth);
                break;
        }

        depthRef.current = newDepth;
        setNavigationDepth(newDepth);
    }, [location.key, navigationType]);
};

export default useNavigationTracker;
